/**
 * Route Mapper Utility
 * Converts friendly database route names to actual React Router paths
 */

// Mapping of friendly names to actual routes
const ROUTE_MAP: Record<string, string> = {
  // Main Apps
  'ImageGenerator': '/image-generator',
  'TaskManager': '/task-manager',
  'GoalTracker': '/task-manager',
  'ContentGenerator': '/project-manager',
  'ResearchGenerator': '/research-generator',
  'AppLibrary': '/app-library',
  
  // Business Apps
  'Contacts': '/contacts',
  'Projects': '/projects',
  'Pipeline': '/pipeline',
  'Notifications': '/notifications',
  
  // Legacy support (in case some routes are already correct)
  '/image-generator': '/image-generator',
  '/task-manager': '/task-manager',
  '/goal-tracker': '/task-manager',
  '/content-generator': '/project-manager',
  '/research-generator': '/research-generator',
  '/app-library': '/app-library',
  '/contacts': '/contacts',
  '/projects': '/projects',
  '/pipeline': '/pipeline',
  '/notifications': '/notifications',
  
  // Legacy route mappings that point to new routes
  '/dashboard/research-matrix': '/research-generator',
  '/dashboard/document-processor': '/project-manager',
};

/**
 * Converts a database route value to the actual React Router path
 * @param dbRoute - The route value stored in the database
 * @returns The actual route path for React Router navigation
 */
export const getActualRoute = (dbRoute: string): string => {
  // Check if we have a direct mapping
  if (ROUTE_MAP[dbRoute]) {
    return ROUTE_MAP[dbRoute];
  }
  
  // If it's already a valid route path (starts with /), use as-is
  if (dbRoute.startsWith('/')) {
    return dbRoute;
  }
  
  // Fallback: convert PascalCase to kebab-case and add leading slash
  const kebabCase = dbRoute
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, ''); // Remove leading dash
  
  return `/${kebabCase}`;
};

/**
 * Gets all available route mappings for reference
 * @returns Object containing all route mappings
 */
export const getAllRouteMappings = (): Record<string, string> => {
  return { ...ROUTE_MAP };
};

/**
 * Validates if a route exists in our mapping
 * @param dbRoute - The route value to validate
 * @returns True if the route has a known mapping
 */
export const isValidRoute = (dbRoute: string): boolean => {
  return dbRoute in ROUTE_MAP || dbRoute.startsWith('/');
};
